
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useResourceProgressInitializer = (
  userId: string | undefined,
  resources: any[] | undefined,
  isTrainee: boolean
) => {
  useEffect(() => {
    if (userId && resources?.length && isTrainee) {
      initializeResourcesProgress(resources, userId);
    }
  }, [userId, resources, isTrainee]);
  
  // Function to initialize progress tracking for all resources
  const initializeResourcesProgress = async (resourcesData: any[], userId: string) => {
    if (!userId || !resourcesData?.length) return;
    
    try {
      console.log('Initializing progress tracking for all resources');
      
      for (const resource of resourcesData) {
        const { data: files, error: filesError } = await supabase
          .from('resource_files')
          .select('id')
          .eq('resource_id', resource.id);
          
        if (filesError) {
          console.error(`Error fetching files for resource ${resource.id}:`, filesError);
          continue;
        }
        
        if (!files?.length) continue;
        
        // Get existing progress records
        const { data: existingProgress, error: progressError } = await supabase
          .from('training_progress')
          .select('file_id')
          .eq('resource_id', resource.id)
          .eq('user_id', userId);
          
        if (progressError) {
          console.error(`Error fetching progress for resource ${resource.id}:`, progressError);
          continue;
        }
        
        // Find files without progress records
        const existingFileIds = new Set(existingProgress?.map(p => p.file_id) || []);
        const filesToInitialize = files.filter(file => !existingFileIds.has(file.id));
        
        if (filesToInitialize.length === 0) continue;
        
        console.log(`Initializing progress for ${filesToInitialize.length} files in resource ${resource.id}`);
        
        // Create progress records in batches to avoid large payloads
        const batchSize = 20;
        for (let i = 0; i < filesToInitialize.length; i += batchSize) {
          const batch = filesToInitialize.slice(i, i + batchSize);
          
          const progressRecords = batch.map(file => ({
            resource_id: resource.id,
            file_id: file.id,
            user_id: userId,
            completed: false,
            updated_at: new Date().toISOString()
          }));
          
          const { error: insertError } = await supabase
            .from('training_progress')
            .insert(progressRecords);
            
          if (insertError) {
            console.error(`Error initializing progress batch for resource ${resource.id}:`, insertError);
          }
        }
      }
    } catch (error) {
      console.error('Error initializing resources progress:', error);
    }
  };
};
